import * as React from "react"
import { cn } from "@/lib/utils"

export interface SocialLinkInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * Icon component to display on the left side
   * @example <EmailIcon size={16} />
   */
  leftIcon?: React.ReactNode
}

/**
 * Social link input with customizable left icon
 *
 * Designed for social network link inputs with icons representing different platforms.
 * Follows ANYW3AR Figma design specifications (node-id: 260-440).
 *
 * @example
 * ```tsx
 * <SocialLinkInput
 *   leftIcon={<EmailIcon size={16} />}
 *   placeholder="contact@anyw3ar.com"
 * />
 * ```
 */
export const SocialLinkInput = React.forwardRef<HTMLInputElement, SocialLinkInputProps>(
  ({ className, leftIcon, ...props }, ref) => {
    return (
      <div
        className={cn(
          "flex items-center",
          "px-2 rounded-[0.3125rem]",
          "bg-white border border-gray-100",
          "transition-colors",
          className
        )}
      >
        {/* Left side: Icon + Input */}
        <div className="flex items-center gap-2 flex-1">
          {/* Left Icon Container - fixed width for consistent alignment */}
          {leftIcon && (
            <div className="flex items-center justify-center py-2 shrink-0 w-4">
              <span className="text-gray-100">
                {leftIcon}
              </span>
            </div>
          )}

          {/* Input Field */}
          <input
            ref={ref}
            className={cn(
              "flex-1 bg-transparent",
              "font-montserrat text-[0.9375rem] font-normal leading-5",
              "text-gray-500 placeholder:text-gray-500",
              "focus:text-gray-900 focus:outline-none",
              "py-2"
            )}
            {...props}
          />
        </div>
      </div>
    )
  }
)

SocialLinkInput.displayName = "SocialLinkInput"
